<template>
  <div class="d-flex flex-column ga-4">
    <v-alert type="info" density="comfortable">
      {{ $t("ncrm.workflows.nodeSettings.mailjetTrigger.hint") }}
    </v-alert>

    <v-select
      v-model="form.accountId"
      :items="accountOptions"
      item-title="label"
      item-value="value"
      :label="$t('ncrm.workflows.nodeSettings.mailjetTrigger.account')"
      :loading="loadingAccounts"
      hide-details="auto"
      :menu-props="selectMenuProps"
    />
    <v-alert v-if="accountsError" type="warning" density="compact">
      {{ accountsError }}
    </v-alert>

    <v-select
      v-model="form.eventTypes"
      :items="eventTypeOptions"
      item-title="label"
      item-value="value"
      multiple
      chips
      :label="$t('ncrm.workflows.nodeSettings.mailjetTrigger.eventTypes')"
      hide-details="auto"
      :menu-props="selectMenuProps"
    />

    <v-text-field
      v-model.number="form.lookbackHours"
      type="number"
      min="1"
      max="720"
      :label="$t('ncrm.workflows.nodeSettings.mailjetTrigger.lookbackHours')"
      hide-details="auto"
    />

    <v-switch
      v-model="form.status"
      :true-value="'active'"
      :false-value="'inactive'"
      color="primary"
      hide-details
      :label="$t('ncrm.workflows.nodeSettings.active')"
    />

    <v-divider />

    <div class="d-flex flex-column ga-3">
      <div>
        <div class="text-body-2 font-weight-semibold ncrm-text-strong">
          {{ $t("ncrm.workflows.nodeSettings.mailjetTrigger.autoRunTitle") }}
        </div>
        <div class="text-caption text-medium-emphasis mt-1">
          {{ $t("ncrm.workflows.nodeSettings.mailjetTrigger.autoRunHint") }}
        </div>
      </div>

      <v-switch
        v-model="form.autoRun"
        color="primary"
        hide-details
        :label="form.autoRun
          ? $t('ncrm.workflows.nodeSettings.autoSyncOn')
          : $t('ncrm.workflows.nodeSettings.autoSyncOff')"
      />

      <v-select
        v-if="form.autoRun"
        v-model="form.autoRunIntervalMinutes"
        :items="intervalOptions"
        item-title="label"
        item-value="value"
        :label="$t('ncrm.workflows.nodeSettings.mailjetTrigger.autoRunInterval')"
        hide-details="auto"
        :menu-props="selectMenuProps"
      />
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { MAILJET_TRIGGER_EVENT_TYPES } from "@/Utile/ncrmMailjetEngagementWorkflow.mjs";

export default {
  name: "NcrmWorkflowMailjetTriggerSettings",
  props: {
    settings: { type: Object, default: () => ({}) },
    intervalOptions: { type: Array, default: () => [] },
    selectMenuProps: { type: Object, default: () => ({ maxHeight: 320, zIndex: 2710 }) },
  },
  emits: ["update:value"],
  data() {
    return {
      form: this.buildForm(this.settings),
      accounts: [],
      loadingAccounts: false,
      accountsError: "",
    };
  },
  computed: {
    accountOptions() {
      const active = this.accounts.filter((a) => a.status === "active");
      return [
        { value: null, label: this.$t("ncrm.workflows.nodeSettings.mailjetTrigger.accountAuto") },
        ...active.map((a) => ({ value: a.id, label: a.name || `#${a.id}` })),
      ];
    },
    eventTypeOptions() {
      return MAILJET_TRIGGER_EVENT_TYPES.map((value) => ({
        value,
        label: this.$t(`ncrm.workflows.nodeSettings.mailjetTrigger.events.${value}`),
      }));
    },
  },
  watch: {
    settings: {
      deep: true,
      handler(next) { this.form = this.buildForm(next); this.emitValue(); },
    },
    form: {
      deep: true,
      handler() { this.emitValue(); },
    },
  },
  mounted() {
    this.fetchAccounts();
    this.emitValue();
  },
  methods: {
    buildForm(settings = {}) {
      const types = Array.isArray(settings.eventTypes) && settings.eventTypes.length
        ? settings.eventTypes.filter((t) => MAILJET_TRIGGER_EVENT_TYPES.includes(t))
        : [...MAILJET_TRIGGER_EVENT_TYPES];
      return {
        status: settings.status || "active",
        accountId: settings.accountId ?? null,
        eventTypes: types.length ? types : [...MAILJET_TRIGGER_EVENT_TYPES],
        lookbackHours: Number(settings.lookbackHours) > 0 ? Math.trunc(Number(settings.lookbackHours)) : 72,
        autoRun: Boolean(settings.autoRun),
        autoRunIntervalMinutes: Number(settings.autoRunIntervalMinutes) > 0
          ? Math.trunc(Number(settings.autoRunIntervalMinutes))
          : 15,
      };
    },
    emitValue() {
      this.$emit("update:value", {
        status: this.form.status,
        accountId: this.form.accountId || null,
        eventTypes: [...this.form.eventTypes],
        lookbackHours: this.form.lookbackHours,
        autoRun: this.form.autoRun,
        autoRunIntervalMinutes: this.form.autoRunIntervalMinutes,
      });
    },
    async fetchAccounts() {
      this.loadingAccounts = true;
      this.accountsError = "";
      try {
        const { data } = await axios.get("/ncrm/integrations/mailjet/accounts");
        this.accounts = Array.isArray(data?.accounts) ? data.accounts : [];
        if (!this.accounts.some((a) => a.status === "active")) {
          this.accountsError = this.$t("ncrm.workflows.nodeSettings.mailjetTrigger.noActiveAccount");
        }
      } catch (err) {
        this.accounts = [];
        this.accountsError = err?.response?.data?.error
          || this.$t("ncrm.workflows.nodeSettings.mailjetTrigger.accountsLoadError");
      } finally {
        this.loadingAccounts = false;
      }
    },
  },
};
</script>
